import { createFeatureHub } from "@feature-hub/core";
import { loadFederatedModule } from "@feature-hub/module-loader-federation";
import { FeatureHubContextProvider } from "@feature-hub/react";
import { defineSerializedStateManager } from "@feature-hub/serialized-state-manager";
import React from "react";
import ReactDOMClient from "react-dom/client";
import { App } from "../app";
import { integratorConfig } from "../config";

declare global {
  interface Window {
    __BASE_URL__: string;
    __FEATURE_APP_INITIAL_TOPIC__?: string;
  }
}

type FeatureAppModuleSource = {
  readonly url: string;
  readonly moduleType?: string;
};

const getUrlsForHydrationFromDom = (): FeatureAppModuleSource[] => {
  const scriptElement = document.querySelector('script[type="x-feature-hub/urls-for-hydration"]');

  if (!scriptElement || !scriptElement.textContent) {
    return [];
  }

  return JSON.parse(scriptElement.textContent);
};

const getSerializedStatesFromDom = (): string | undefined => {
  const scriptElement = document.querySelector('script[type="x-feature-hub/serialized-states"]');

  return scriptElement?.textContent || undefined;
};

(async () => {
  const baseUrl = window.__BASE_URL__;
  const initialTopic = window.__FEATURE_APP_INITIAL_TOPIC__;

  const serializedStates = getSerializedStatesFromDom();
  const { featureAppManager } = createFeatureHub(integratorConfig.integratorId, {
    moduleLoader: loadFederatedModule,
    featureServiceDefinitions: [defineSerializedStateManager(serializedStates)],
  });

  const urlsForHydration = getUrlsForHydrationFromDom();

  await Promise.all(
    urlsForHydration.map(async ({ url, moduleType }) =>
      featureAppManager.preloadFeatureApp(url, moduleType)
    )
  );

  return ReactDOMClient.hydrateRoot(
    document.getElementById("feature-app")!,
    <React.StrictMode>
      <FeatureHubContextProvider value={{ featureAppManager }}>
        <App baseUrl={baseUrl} initialTopic={initialTopic} />
      </FeatureHubContextProvider>
    </React.StrictMode>
  );
  // eslint-disable-next-line no-console
})().catch(console.error);
